import { useCallback, useEffect, useRef, useState } from 'react';
import {
  linkApplyAllowed,
  normalizeLinkHref,
} from '../utils/editorHelpers';

/**
 * State machine for the link panel used by FormatChrome.
 *
 * Owns:
 *   - linkUrl / linkText (controlled inputs)
 *   - anchorStyle (captured once when the dock panel opens so scrolling the
 *     editor doesn't drag the panel around)
 *   - open/cancel/apply transitions + notifies parent via onLinkModeChange
 *
 * The parent wires refs for the two inputs + a ref to the panel wrapper so
 * focus management and outside-click handling live at the component level
 * where the DOM is rendered.
 */
export default function useLinkMode({
  editor,
  layout,
  onLinkModeChange,
  onCollapseFormatting,
}) {
  const [linkMode, setLinkMode] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [anchorStyle, setAnchorStyle] = useState(null);

  const linkTextInputRef = useRef(null);
  const linkUrlInputRef = useRef(null);
  const panelRef = useRef(null);

  const setLinkModeAndNotify = useCallback(
    (next) => {
      setLinkMode(next);
      onLinkModeChange?.(next);
    },
    [onLinkModeChange]
  );

  const cancelLinkMode = useCallback(() => {
    setLinkModeAndNotify(false);
    setLinkUrl('');
    setLinkText('');
    setAnchorStyle(null);
    editor?.commands.focus();
  }, [editor, setLinkModeAndNotify]);

  // Auto-focus the right field when link mode opens.
  useEffect(() => {
    if (!linkMode || !editor) return;
    requestAnimationFrame(() => {
      if (editor.state.selection.empty) {
        linkTextInputRef.current?.focus();
      } else {
        linkUrlInputRef.current?.focus();
      }
    });
  }, [linkMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectionEmpty = editor?.state.selection.empty ?? true;
  const canApplyLink = editor
    ? linkApplyAllowed(editor, selectionEmpty, linkText, linkUrl)
    : false;

  const applyLink = useCallback(() => {
    if (!editor) return;
    const empty = editor.state.selection.empty;
    if (!linkApplyAllowed(editor, empty, linkText, linkUrl)) return;
    if (empty) {
      const text = linkText.trim();
      const href = normalizeLinkHref(linkUrl);
      if (!text || !href) return;
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'text',
          text,
          marks: [
            {
              type: 'link',
              attrs: {
                href,
                target: '_blank',
                rel: 'noopener noreferrer',
              },
            },
          ],
        })
        .run();
    } else {
      const hrefRaw = linkUrl.trim();
      if (!hrefRaw) {
        editor.chain().focus().unsetLink().run();
      } else {
        editor
          .chain()
          .focus()
          .setLink({
            href: normalizeLinkHref(hrefRaw),
            target: '_blank',
            rel: 'noopener noreferrer',
          })
          .run();
      }
    }
    setLinkModeAndNotify(false);
    setLinkUrl('');
    setLinkText('');
    setAnchorStyle(null);
  }, [editor, linkText, linkUrl, setLinkModeAndNotify]);

  // Capture cursor screen position once (dock layout only) and open link mode.
  const openLinkMode = useCallback(() => {
    if (!editor) return;
    if (layout === 'dock') {
      try {
        const { from, to } = editor.state.selection;
        const view = editor.view;
        const startCoords = view.coordsAtPos(from);
        const endCoords = editor.state.selection.empty
          ? startCoords
          : view.coordsAtPos(to);
        const midX = (startCoords.left + endCoords.right) / 2;
        const GAP = 8;
        const PANEL_W = Math.min(380, window.innerWidth - 24);
        const showAbove =
          startCoords.top > window.innerHeight - startCoords.bottom &&
          startCoords.top >= 110;
        setAnchorStyle({
          width: PANEL_W,
          left: Math.min(
            Math.max(midX, 12 + PANEL_W / 2),
            window.innerWidth - 12 - PANEL_W / 2
          ),
          transform: 'translateX(-50%)',
          ...(showAbove
            ? { bottom: window.innerHeight - startCoords.top + GAP }
            : { top: startCoords.bottom + GAP }),
        });
      } catch {
        setAnchorStyle(null); // fall back: panel won't render (edge case)
      }
    }
    onCollapseFormatting?.();
    setLinkUrl(editor.getAttributes('link').href || '');
    setLinkText('');
    setLinkModeAndNotify(true);
  }, [editor, layout, onCollapseFormatting, setLinkModeAndNotify]);

  return {
    // state
    linkMode,
    linkUrl,
    linkText,
    anchorStyle,
    selectionEmpty,
    canApplyLink,
    // refs
    linkTextInputRef,
    linkUrlInputRef,
    panelRef,
    // actions
    setLinkUrl,
    setLinkText,
    openLinkMode,
    cancelLinkMode,
    applyLink,
  };
}
